import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import { createElement, Fragment, type ReactNode } from "react";

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        "text-heading-2xl",
        "text-heading-lg",
        "text-heading-mdlg",
        "text-heading-md",
        "text-heading-sm",
        "text-body-md",
        "text-body-sm",
        "text-body-xs",
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Rich text ───────────────────────────────────────────────
// Parses a tiny markdown subset: **bold**, *italic*, and \n line breaks.
// Returns React elements — no dangerouslySetInnerHTML, no XSS surface.
// Unicode characters (em dash, etc.) pass through unchanged.

const INLINE_RE = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;

function parseInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2] != null) {
      // **bold**
      parts.push(createElement("strong", { key: match.index }, match[2]));
    } else if (match[3] != null) {
      // *italic*
      parts.push(createElement("em", { key: match.index }, match[3]));
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/**
 * Render a string with basic formatting into React elements.
 *
 * Supported:
 * - `**bold**` → `<strong>`
 * - `*italic*` → `<em>`
 * - Newlines → `<br />`
 * - Unicode characters (em dash `—`, etc.) pass through as-is
 *
 * Returns the original string unchanged when no formatting is present
 * (no extra wrapper elements).
 */
export function renderRichText(text: string): ReactNode {
  const hasFormatting = text.includes("\n") || INLINE_RE.test(text);
  if (!hasFormatting) return text;

  // Reset regex lastIndex after the test above
  INLINE_RE.lastIndex = 0;

  const lines = text.split("\n");
  const nodes: ReactNode[] = [];
  lines.forEach((line, i) => {
    if (i > 0) nodes.push(createElement("br", { key: `br-${i}` }));
    nodes.push(...parseInline(line));
  });
  return createElement(Fragment, null, ...nodes);
}
